import { useEffect } from 'react';
import type { Account, AccountAddress } from '../lib/accounts';
import { fullAddress } from '../lib/accounts';

function Field({ k, v }: { k: string; v?: string | number }) {
  if (v === undefined || v === '' || v === null) return null;
  return <div className="field"><div className="field-k">{k}</div><div className="field-v">{v}</div></div>;
}

function AddrCard({ label, addr }: { label: string; addr?: AccountAddress }) {
  const full = fullAddress(addr);
  if (!full) return null;
  return (
    <div className="addr-card">
      <div className="lbl">{label}</div>
      {addr?.street && <div>{addr.street}</div>}
      <div>{[addr?.city, addr?.state, addr?.postal].filter(Boolean).join(', ')}</div>
      {addr?.country && <div>{addr.country}</div>}
    </div>
  );
}

export default function AccountDrawer({ account, onClose }: { account: Account; onClose: () => void }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const a = account;
  const isVendor = a.type === 'vendor';

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-head">
          <button className="drawer-close" onClick={onClose}>×</button>
          <div className="drawer-title">{a.name}</div>
          <div className="drawer-tags">
            <span className={`pill pill-${a.type}`}>{a.type}</span>
            <span className={`pill pill-${a.status}`}>{a.status}</span>
            {a.source && <span className="pill pill-inactive">{a.source}</span>}
          </div>
        </div>

        <div className="drawer-body">
          <div className="section-h">Overview</div>
          <Field k="Account ID" v={a.id} />
          <Field k="Type" v={a.type} />
          <Field k="Status" v={a.status} />
          <Field k="Industry" v={a.industry} />
          {isVendor && <Field k="Category" v={a.category} />}
          {isVendor && <Field k="Avg lead time" v={a.avgLeadDays != null ? `${a.avgLeadDays} days` : undefined} />}
          {a.healthScore != null && <Field k="Health score" v={a.healthScore} />}
          <Field k="Origin" v={a._origin} />

          {(a.contactName || a.email || a.phone) && (
            <>
              <div className="section-h">Contact</div>
              <Field k="Primary contact" v={a.contactName} />
              <Field k="Email" v={a.email} />
              <Field k="Phone" v={a.phone} />
            </>
          )}

          {isVendor && a.materials?.length ? (
            <>
              <div className="section-h">Supplies</div>
              <div className="drawer-tags">
                {a.materials.map((m) => <span key={m} className="pill pill-vendor">{m}</span>)}
              </div>
            </>
          ) : null}

          {(fullAddress(a.billing) || fullAddress(a.shipping)) && (
            <>
              <div className="section-h">Addresses</div>
              <AddrCard label="Billing" addr={a.billing} />
              <AddrCard label="Shipping" addr={a.shipping} />
            </>
          )}

          {a.notes && (
            <>
              <div className="section-h">Notes</div>
              <div style={{ fontSize: 13, color: 'var(--tx2)', lineHeight: 1.6 }}>{a.notes}</div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}
